const COMPARISON = [
    {
        product: "Induce XT",
        company: "Induce Biologics",
        composition:
            "NMP-processed bone allograft — human cortical bone fibers and microparticulates with mineralized cancellous bone.",
        source: { label: "inducext.com", href: "https://www.inducext.com/" },
        highlight: true,
    },
    {
        product: "Augment",
        company: "Stryker",
        composition: "Recombinant PDGF-BB with a β-TCP scaffold.",
        source: { label: "stryker.com", href: "https://www.stryker.com/" },
    },
    {
        product: "ArthroCell",
        company: "Arthrex",
        composition: "Viable cellular bone allograft.",
        source: { label: "arthrex.com", href: "https://www.arthrex.com/" },
    },
    {
        product: "ViviGen",
        company: "LifeNet Health / DePuy Synthes",
        composition: "Cellular bone matrix.",
        source: { label: "jnjmedtech.com", href: "https://www.jnjmedtech.com/" },
    },
    {
        product: "BIO4",
        company: "Stryker",
        composition: "Viable bone matrix.",
        source: { label: "stryker.com", href: "https://www.stryker.com/" },
    },
];

/**
 * Product comparison table — Induce XT vs. category alternatives.
 * Reused on /inducext and /for-reps.
 */
export default function ProductComparison() {
    return (
        <section
            data-testid="inducext-competitive-section"
            className="border-b border-slate-200 bg-white"
        >
            <div className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-20">
                <div className="max-w-3xl">
                    <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
                        Product Comparison
                    </p>
                    <h2 className="mt-3 font-display text-3xl md:text-[32px] font-semibold tracking-tight text-slate-900 leading-[1.15]">
                        Know the alternatives.
                    </h2>
                    <p className="mt-3 text-[15px] md:text-base text-slate-600 leading-relaxed">
                        Compare product categories and core composition.
                    </p>
                </div>

                <div className="mt-8 rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.08)] overflow-hidden">
                    {/* Desktop table */}
                    <div className="hidden md:block overflow-x-auto">
                        <table
                            data-testid="inducext-competitor-table"
                            className="w-full text-[15px]"
                        >
                            <thead>
                                <tr className="text-left text-[11px] font-medium uppercase tracking-[0.14em] text-slate-500 bg-slate-50">
                                    <th className="px-6 md:px-8 py-3 border-b border-slate-200 font-medium w-[28%]">
                                        Product
                                    </th>
                                    <th className="px-6 md:px-8 py-3 border-b border-slate-200 font-medium w-[28%]">
                                        Company / role
                                    </th>
                                    <th className="px-6 md:px-8 py-3 border-b border-slate-200 font-medium">
                                        Category &amp; composition
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {COMPARISON.map((c, idx) => {
                                    const last = idx === COMPARISON.length - 1;
                                    const rowId = c.product.toLowerCase().replace(/\s+/g, "-");
                                    return (
                                        <tr
                                            key={c.product}
                                            data-testid={`inducext-competitor-row-${rowId}`}
                                            className={c.highlight ? "bg-slate-50" : "bg-white"}
                                        >
                                            <td className={`px-6 md:px-8 py-5 align-top ${last ? "" : "border-b border-slate-100"}`}>
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <span className="text-slate-900 font-semibold">
                                                        {c.product}
                                                    </span>
                                                    {c.highlight && (
                                                        <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.14em] rounded bg-slate-900 text-white">
                                                            This product
                                                        </span>
                                                    )}
                                                </div>
                                                {c.source && (
                                                    <a
                                                        href={c.source.href}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        data-testid={`inducext-competitor-source-${rowId}`}
                                                        className="mt-2 inline-flex items-center gap-1 text-[12px] text-slate-500 hover:text-slate-900 underline decoration-slate-300 hover:decoration-slate-900 underline-offset-4 transition-colors"
                                                    >
                                                        Source: {c.source.label}
                                                    </a>
                                                )}
                                            </td>
                                            <td className={`px-6 md:px-8 py-5 align-top text-slate-700 ${last ? "" : "border-b border-slate-100"}`}>
                                                {c.company}
                                            </td>
                                            <td className={`px-6 md:px-8 py-5 align-top text-slate-700 leading-relaxed ${last ? "" : "border-b border-slate-100"}`}>
                                                {c.composition}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile cards */}
                    <ul className="md:hidden divide-y divide-slate-100">
                        {COMPARISON.map((c) => {
                            const rowId = c.product.toLowerCase().replace(/\s+/g, "-");
                            return (
                                <li
                                    key={c.product}
                                    data-testid={`inducext-competitor-mobile-${rowId}`}
                                    className={`px-6 py-5 ${c.highlight ? "bg-slate-50" : "bg-white"}`}
                                >
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-slate-900 font-semibold text-base">
                                            {c.product}
                                        </span>
                                        {c.highlight && (
                                            <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.14em] rounded bg-slate-900 text-white">
                                                This product
                                            </span>
                                        )}
                                    </div>
                                    <dl className="mt-3 space-y-2">
                                        <div>
                                            <dt className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-500">
                                                Company / role
                                            </dt>
                                            <dd className="mt-0.5 text-[15px] text-slate-700">
                                                {c.company}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-500">
                                                Category &amp; composition
                                            </dt>
                                            <dd className="mt-0.5 text-[15px] text-slate-700 leading-relaxed">
                                                {c.composition}
                                            </dd>
                                        </div>
                                    </dl>
                                    {c.source && (
                                        <a
                                            href={c.source.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid={`inducext-competitor-source-${rowId}`}
                                            className="mt-3 inline-flex items-center gap-1 text-[12px] text-slate-500 hover:text-slate-900 underline decoration-slate-300 hover:decoration-slate-900 underline-offset-4 transition-colors"
                                        >
                                            Source: {c.source.label}
                                        </a>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>

                <p className="mt-5 text-sm text-slate-500 leading-relaxed max-w-3xl">
                    Inclusion does not imply equivalent indications, clinical
                    evidence, or performance. Products differ in composition,
                    indications, and clinical evidence, and are not interchangeable
                    in every case.
                </p>
            </div>
        </section>
    );
}
